import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Shield, Trash2, Key, Trophy, Pencil, X, Target } from 'lucide-react';
import { format } from 'date-fns';

export default function AllUsersTab({ 
  users, 
  onRefresh,
  onViewScores 
}) {
  const [editingName, setEditingName] = useState(null);
  const [newName, setNewName] = useState('');

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/admin-rights`, {
        is_admin: !currentStatus
      });
      toast.success(currentStatus ? 'Admin rights removed' : 'Admin rights granted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin rights');
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = window.prompt('Enter new password for this user:');
    if (!newPassword) return;
    
    try {
      await axios.put(`${API}/admin/users/${userId}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditName = (user) => {
    setEditingName(user);
    setNewName(user.name);
  };

  const handleCancelNameEdit = () => {
    setEditingName(null);
    setNewName('');
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    if (newName.trim() === editingName.name) {
      handleCancelNameEdit();
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${editingName.id}/name`, {
        new_name: newName.trim()
      });
      toast.success('Name updated and scores recalculated!');
      handleCancelNameEdit();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update name');
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-stone-100 border-b border-stone-200">
            <tr>
              <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Name</th>
              <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Email</th>
              <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Role</th>
              <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Registered</th>
              <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-stone-100 hover:bg-stone-50 group">
                <td className="py-4 px-4 font-inter text-sm text-slate-900 font-medium">
                  {editingName?.id === u.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="h-8 w-48 text-sm"
                        autoFocus
                        data-testid={`edit-name-input-${u.id}`}
                      />
                      <Button
                        onClick={handleSaveName}
                        variant="ghost"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 rounded-sm p-1"
                        title="Save"
                        data-testid={`save-name-${u.id}`}
                      >
                        <Target className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        onClick={handleCancelNameEdit}
                        variant="ghost"
                        className="text-stone-600 hover:text-stone-700 hover:bg-stone-100 rounded-sm p-1"
                        title="Cancel"
                        data-testid={`cancel-name-${u.id}`}
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>{u.name}</span>
                      <Button
                        onClick={() => handleEditName(u)}
                        variant="ghost"
                        className="text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit Name"
                        data-testid={`edit-name-btn-${u.id}`}
                      >
                        <Pencil className="w-3 h-3" strokeWidth={1.5} />
                      </Button>
                    </div>
                  )}
                </td>
                <td className="py-4 px-4 font-inter text-sm text-stone-600">{u.email}</td>
                <td className="py-4 px-4 text-center">
                  {u.is_admin ? (
                    <span className="inline-block bg-amber-600 text-white font-barlow font-semibold text-xs px-3 py-1 rounded-sm uppercase">
                      Admin
                    </span>
                  ) : (
                    <span className="inline-block bg-stone-200 text-slate-700 font-barlow font-semibold text-xs px-3 py-1 rounded-sm uppercase">
                      User
                    </span>
                  )}
                </td>
                <td className="py-4 px-4 text-center font-inter text-sm text-stone-600">
                  {format(new Date(u.created_at), 'MMM dd, yyyy')}
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      onClick={() => handleEditName(u)}
                      variant="ghost"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-sm p-2"
                      title="Edit Name"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      onClick={() => onViewScores(u)}
                      variant="ghost"
                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-sm p-2"
                      title="View Scores"
                    >
                      <Trophy className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                      variant="ghost"
                      className={`rounded-sm p-2 ${
                        u.is_admin 
                          ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' 
                          : 'text-stone-400 hover:text-amber-600 hover:bg-amber-50'
                      }`}
                      title={u.is_admin ? 'Remove Admin' : 'Make Admin'}
                    >
                      <Shield className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      onClick={() => handleResetPassword(u.id)}
                      variant="ghost"
                      className="text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm p-2"
                      title="Reset Password"
                    >
                      <Key className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      onClick={() => handleDeleteUser(u.id, u.name)}
                      variant="ghost"
                      className="text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-sm p-2"
                      title="Delete User"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
